(function () {
  'use strict';

  function isNumber(subject) {
    return typeof subject === 'number';
  }
  function isString(subject) {
    return typeof subject === 'string';
  }
  function isBoolean(subject) {
    return typeof subject === 'boolean';
  }
  function isObject(subject) {
    return Object.prototype.toString.call(subject) === '[object Object]';
  }
  function mathAbs(n) {
    return Math.abs(n);
  }
  function mathSign(n) {
    return Math.sign(n);
  }
  function deltaAbs(valueB, valueA) {
    return mathAbs(valueB - valueA);
  }
  function factorAbs(valueB, valueA) {
    if (valueB === 0 || valueA === 0) return 0;
    if (mathAbs(valueB) <= mathAbs(valueA)) return 0;
    const diff = deltaAbs(mathAbs(valueB), mathAbs(valueA));
    return mathAbs(diff / valueB);
  }
  function roundToTwoDecimals(num) {
    return Math.round(num * 100) / 100;
  }
  function arrayKeys(array) {
    return objectKeys(array).map(Number);
  }
  function arrayLast(array) {
    return array[arrayLastIndex(array)];
  }
  function arrayLastIndex(array) {
    return Math.max(0, array.length - 1);
  }
  function arrayIsLastIndex(array, index) {
    return index === arrayLastIndex(array);
  }
  function arrayFromNumber(n, startAt = 0) {
    return Array.from(Array(n), (_, i) => startAt + i);
  }
  function objectKeys(object) {
    return Object.keys(object);
  }
  function objectsMergeDeep(objectA, objectB) {
    return [objectA, objectB].reduce((mergedObjects, currentObject) => {
      objectKeys(currentObject).forEach(key => {
        const valueA = mergedObjects[key];
        const valueB = currentObject[key];
        const areObjects = isObject(valueA) && isObject(valueB);
        mergedObjects[key] = areObjects ? objectsMergeDeep(valueA, valueB) : valueB;
      });
      return mergedObjects;
    }, {});
  }
  function isMouseEvent(evt, ownerWindow) {
    return typeof ownerWindow.MouseEvent !== 'undefined' && evt instanceof ownerWindow.MouseEvent;
  }
  function Alignment(align, viewSize) {
    const predefined = {
      start,
      center,
      end
    };
    function start() {
      return 0;
    }
    function center(n) {
      return end(n) / 2;
    }
    function end(n) {
      return viewSize - n;
    }
    function measure(n, index) {
      if (isString(align)) return predefined[align](n);
      return align(viewSize, n, index);
    }
    const self = {
      measure
    };
    return self;
  }
  function EventStore() {
    let listeners = [];
    function add(node, type, handler, options = {
      passive: true
    }) {
      let removeListener;
      if ('addEventListener' in node) {
        node.addEventListener(type, handler, options);
        removeListener = () => node.removeEventListener(type, handler, options);
      } else {
        const legacyMediaQueryList = node;
        legacyMediaQueryList.addListener(handler);
        removeListener = () => legacyMediaQueryList.removeListener(handler);
      }
      listeners.push(removeListener);
      return self;
    }
    function clear() {
      listeners = listeners.filter(remove => remove());
    }
    const self = {
      add,
      clear
    };
    return self;
  }
  function Animations(ownerDocument, ownerWindow, update, render) {
    const documentVisibleHandler = EventStore();
    const fixedTimeStep = 1000 / 60;
    let lastTimeStamp = null;
    let accumulatedTime = 0;
    let animationId = 0;
    function init() {
      documentVisibleHandler.add(ownerDocument, 'visibilitychange', () => {
        if (ownerDocument.hidden) reset();
      });
    }
    function destroy() {
      stop();
      documentVisibleHandler.clear();
    }
    function animate(timeStamp) {
      if (!animationId) return;
      if (!lastTimeStamp) {
        lastTimeStamp = timeStamp;
        update();
        update();
      }
      const timeElapsed = timeStamp - lastTimeStamp;
      lastTimeStamp = timeStamp;
      accumulatedTime += timeElapsed;
      while (accumulatedTime >= fixedTimeStep) {
        update();
        accumulatedTime -= fixedTimeStep;
      }
      const alpha = accumulatedTime / fixedTimeStep;
      render(alpha);
      if (animationId) {
        animationId = ownerWindow.requestAnimationFrame(animate);
      }
    }
    function start() {
      if (animationId) return;
      animationId = ownerWindow.requestAnimationFrame(animate);
    }
    function stop() {
      ownerWindow.cancelAnimationFrame(animationId);
      lastTimeStamp = null;
      accumulatedTime = 0;
      animationId = 0;
    }
    function reset() {
      lastTimeStamp = null;
      accumulatedTime = 0;
    }
    const self = {
      init,
      destroy,
      start,
      stop,
      update,
      render
    };
    return self;
  }
  function Axis(axis, contentDirection) {
    const isRightToLeft = contentDirection === 'rtl';
    const isVertical = axis === 'y';
    const scroll = isVertical ? 'y' : 'x';
    const cross = isVertical ? 'x' : 'y';
    const sign = !isVertical && isRightToLeft ? -1 : 1;
    const startEdge = getStartEdge();
    const endEdge = getEndEdge();
    function measureSize(nodeRect) {
      const {
        height,
        width
      } = nodeRect;
      return isVertical ? height : width;
    }
    function getStartEdge() {
      if (isVertical) return 'top';
      return isRightToLeft ? 'right' : 'left';
    }
    function getEndEdge() {
      if (isVertical) return 'bottom';
      return isRightToLeft ? 'left' : 'right';
    }
    function direction(n) {
      return n * sign;
    }
    const self = {
      scroll,
      cross,
      startEdge,
      endEdge,
      measureSize,
      direction
    };
    return self;
  }
  function Limit(min = 0, max = 0) {
    const length = mathAbs(min - max);
    function reachedMin(n) {
      return n < min;
    }
    function reachedMax(n) {
      return n > max;
    }
    function reachedAny(n) {
      return reachedMin(n) || reachedMax(n);
    }
    function constrain(n) {
      if (!reachedAny(n)) return n;
      return reachedMin(n) ? min : max;
    }
    function removeOffset(n) {
      if (!length) return n;
      return n - length * Math.ceil((n - max) / length);
    }
    const self = {
      length,
      max,
      min,
      constrain,
      reachedAny,
      reachedMax,
      reachedMin,
      removeOffset
    };
    return self;
  }
  function Counter(max, start, loop) {
    const {
      constrain
    } = Limit(0, max);
    const loopEnd = max + 1;
    let counter = withinLimit(start);
    function withinLimit(n) {
      return !loop ? constrain(n) : mathAbs((loopEnd + n) % loopEnd);
    }
    function get() {
      return counter;
    }
    function set(n) {
      counter = withinLimit(n);
      return self;
    }
    function add(n) {
      return clone().set(get() + n);
    }
    function clone() {
      return Counter(max, get(), loop);
    }
    const self = {
      get,
      set,
      add,
      clone
    };
    return self;
  }
  function DragHandler(axis, rootNode, ownerDocument, ownerWindow, target, dragTracker, location, animation, scrollTo, scrollBody, scrollTarget, index, eventHandler, percentOfView, dragFree, dragThreshold, skipSnaps, baseFriction, watchDrag) {
    const {
      cross: crossAxis,
      direction
    } = axis;
    const focusNodes = ['INPUT', 'SELECT', 'TEXTAREA'];
    const nonPassiveEvent = {
      passive: false
    };
    const initEvents = EventStore();
    const dragEvents = EventStore();
    const goToNextThreshold = Limit(50, 225).constrain(percentOfView.measure(20));
    const snapForceBoost = {
      mouse: 300,
      touch: 400
    };
    const freeForceBoost = {
      mouse: 500,
      touch: 600
    };
    const baseSpeed = dragFree ? 43 : 25;
    let isMoving = false;
    let startScroll = 0;
    let startCross = 0;
    let pointerIsDown = false;
    let preventScroll = false;
    let preventClick = false;
    let isMouse = false;
    function init(emblaApi) {
      if (!watchDrag) return;
      function downIfAllowed(evt) {
        if (isBoolean(watchDrag) || watchDrag(emblaApi, evt)) down(evt);
      }
      const node = rootNode;
      initEvents.add(node, 'dragstart', evt => evt.preventDefault(), nonPassiveEvent).add(node, 'touchmove', () => undefined, nonPassiveEvent).add(node, 'touchend', () => undefined).add(node, 'touchstart', downIfAllowed).add(node, 'mousedown', downIfAllowed).add(node, 'touchcancel', up).add(node, 'contextmenu', up).add(node, 'click', click, true);
    }
    function destroy() {
      initEvents.clear();
      dragEvents.clear();
    }
    function addDragEvents() {
      const node = isMouse ? ownerDocument : rootNode;
      dragEvents.add(node, 'touchmove', move, nonPassiveEvent).add(node, 'touchend', up).add(node, 'mousemove', move, nonPassiveEvent).add(node, 'mouseup', up);
    }
    function isFocusNode(node) {
      const nodeName = node.nodeName || '';
      return focusNodes.includes(nodeName);
    }
    function forceBoost() {
      const boost = dragFree ? freeForceBoost : snapForceBoost;
      const type = isMouse ? 'mouse' : 'touch';
      return boost[type];
    }
    function allowedForce(force, targetChanged) {
      const next = index.add(mathSign(force) * -1);
      const baseForce = scrollTarget.byDistance(force, !dragFree).distance;
      if (dragFree || mathAbs(force) < goToNextThreshold) return baseForce;
      if (skipSnaps && targetChanged) return baseForce * 0.5;
      return scrollTarget.byIndex(next.get(), 0).distance;
    }
    function down(evt) {
      const isMouseEvt = isMouseEvent(evt, ownerWindow);
      isMouse = isMouseEvt;
      preventClick = dragFree && isMouseEvt && !evt.buttons && isMoving;
      isMoving = deltaAbs(target.get(), location.get()) >= 2;
      if (isMouseEvt && evt.button !== 0) return;
      if (isFocusNode(evt.target)) return;
      pointerIsDown = true;
      dragTracker.pointerDown(evt);
      scrollBody.useFriction(0).useDuration(0);
      target.set(location);
      addDragEvents();
      startScroll = dragTracker.readPoint(evt);
      startCross = dragTracker.readPoint(evt, crossAxis);
      eventHandler.emit('pointerDown');
    }
    function move(evt) {
      const isTouchEvt = !isMouseEvent(evt, ownerWindow);
      if (isTouchEvt && evt.touches.length >= 2) return up(evt);
      const lastScroll = dragTracker.readPoint(evt);
      const lastCross = dragTracker.readPoint(evt, crossAxis);
      const diffScroll = deltaAbs(lastScroll, startScroll);
      const diffCross = deltaAbs(lastCross, startCross);
      if (!preventScroll && !isMouse) {
        if (!evt.cancelable) return up(evt);
        preventScroll = diffScroll > diffCross;
        if (!preventScroll) return up(evt);
      }
      const diff = dragTracker.pointerMove(evt);
      if (diffScroll > dragThreshold) preventClick = true;
      scrollBody.useFriction(0.3).useDuration(0.75);
      animation.start();
      target.add(direction(diff));
      evt.preventDefault();
    }
    function up(evt) {
      const currentLocation = scrollTarget.byDistance(0, false);
      const targetChanged = currentLocation.index !== index.get();
      const rawForce = dragTracker.pointerUp(evt) * forceBoost();
      const force = allowedForce(direction(rawForce), targetChanged);
      const forceFactor = factorAbs(rawForce, force);
      const speed = baseSpeed - 10 * forceFactor;
      const friction = baseFriction + forceFactor / 50;
      preventScroll = false;
      pointerIsDown = false;
      dragEvents.clear();
      scrollBody.useDuration(speed).useFriction(friction);
      scrollTo.distance(force, !dragFree);
      isMouse = false;
      eventHandler.emit('pointerUp');
    }
    function click(evt) {
      if (preventClick) {
        evt.stopPropagation();
        evt.preventDefault();
        preventClick = false;
      }
    }
    function pointerDown() {
      return pointerIsDown;
    }
    const self = {
      init,
      destroy,
      pointerDown
    };
    return self;
  }
  function DragTracker(axis, ownerWindow) {
    const logInterval = 170;
    let startEvent;
    let lastEvent;
    function readTime(evt) {
      return evt.timeStamp;
    }
    function readPoint(evt, evtAxis) {
      const property = evtAxis || axis.scroll;
      const coord = `client${property === 'x' ? 'X' : 'Y'}`;
      return (isMouseEvent(evt, ownerWindow) ? evt : evt.touches[0])[coord];
    }
    function pointerDown(evt) {
      startEvent = evt;
      lastEvent = evt;
      return readPoint(evt);
    }
    function pointerMove(evt) {
      const diff = readPoint(evt) - readPoint(lastEvent);
      const expired = readTime(evt) - readTime(startEvent) > logInterval;
      lastEvent = evt;
      if (expired) startEvent = evt;
      return diff;
    }
    function pointerUp(evt) {
      if (!startEvent || !lastEvent) return 0;
      const diffDrag = readPoint(lastEvent) - readPoint(startEvent);
      const diffTime = readTime(evt) - readTime(startEvent);
      const expired = readTime(evt) - readTime(lastEvent) > logInterval;
      const force = diffDrag / diffTime;
      const isFlick = diffTime && !expired && mathAbs(force) > 0.1;
      return isFlick ? force : 0;
    }
    const self = {
      pointerDown,
      pointerMove,
      pointerUp,
      readPoint
    };
    return self;
  }
  function NodeRects() {
    function measure(node) {
      const {
        offsetTop,
        offsetLeft,
        offsetWidth,
        offsetHeight
      } = node;
      const offset = {
        top: offsetTop,
        right: offsetLeft + offsetWidth,
        bottom: offsetTop + offsetHeight,
        left: offsetLeft,
        width: offsetWidth,
        height: offsetHeight
      };
      return offset;
    }
    const self = {
      measure
    };
    return self;
  }
  function PercentOfView(viewSize) {
    function measure(n) {
      return viewSize * (n / 100);
    }
    const self = {
      measure
    };
    return self;
  }
  function ResizeHandler(container, eventHandler, ownerWindow, slides, axis, watchResize, nodeRects) {
    const observeNodes = [container].concat(slides);
    let resizeObserver;
    let containerSize;
    let slideSizes = [];
    let destroyed = false;
    function readSize(node) {
      return axis.measureSize(nodeRects.measure(node));
    }
    function init(emblaApi) {
      if (!watchResize) return;
      containerSize = readSize(container);
      slideSizes = slides.map(readSize);
      function defaultCallback(entries) {
        for (const entry of entries) {
          if (destroyed) return;
          const isContainer = entry.target === container;
          const slideIndex = slides.indexOf(entry.target);
          const lastSize = isContainer ? containerSize : slideSizes[slideIndex];
          const newSize = readSize(isContainer ? container : slides[slideIndex]);
          const diffSize = mathAbs(newSize - lastSize);
          if (diffSize >= 0.5) {
            emblaApi.reInit();
            eventHandler.emit('resize');
            break;
          }
        }
      }
      resizeObserver = new ResizeObserver(entries => {
        if (isBoolean(watchResize) || watchResize(emblaApi, entries)) {
          defaultCallback(entries);
        }
      });
      ownerWindow.requestAnimationFrame(() => {
        observeNodes.forEach(node => resizeObserver.observe(node));
      });
    }
    function destroy() {
      destroyed = true;
      if (resizeObserver) resizeObserver.disconnect();
    }
    const self = {
      init,
      destroy
    };
    return self;
  }
  function ScrollBody(location, offsetLocation, previousLocation, target, baseDuration, baseFriction) {
    let scrollVelocity = 0;
    let scrollDirection = 0;
    let scrollDuration = baseDuration;
    let scrollFriction = baseFriction;
    let rawLocation = location.get();
    let rawLocationPrevious = 0;
    function seek() {
      const displacement = target.get() - location.get();
      const isInstant = !scrollDuration;
      let scrollDistance = 0;
      if (isInstant) {
        scrollVelocity = 0;
        previousLocation.set(target);
        location.set(target);
        scrollDistance = displacement;
      } else {
        previousLocation.set(location);
        scrollVelocity += displacement / scrollDuration;
        scrollVelocity *= scrollFriction;
        rawLocation += scrollVelocity;
        location.add(scrollVelocity);
        scrollDistance = rawLocation - rawLocationPrevious;
      }
      scrollDirection = mathSign(scrollDistance);
      rawLocationPrevious = rawLocation;
      return self;
    }
    function settled() {
      const diff = target.get() - offsetLocation.get();
      return mathAbs(diff) < 0.001;
    }
    function duration() {
      return scrollDuration;
    }
    function direction() {
      return scrollDirection;
    }
    function velocity() {
      return scrollVelocity;
    }
    function useBaseDuration() {
      return useDuration(baseDuration);
    }
    function useBaseFriction() {
      return useFriction(baseFriction);
    }
    function useDuration(n) {
      scrollDuration = n;
      return self;
    }
    function useFriction(n) {
      scrollFriction = n;
      return self;
    }
    const self = {
      direction,
      duration,
      velocity,
      seek,
      settled,
      useBaseFriction,
      useBaseDuration,
      useFriction,
      useDuration
    };
    return self;
  }
  function ScrollBounds(limit, location, target, scrollBody, percentOfView) {
    const pullBackThreshold = percentOfView.measure(10);
    const edgeOffsetTolerance = percentOfView.measure(50);
    const frictionLimit = Limit(0.1, 0.99);
    let disabled = false;
    function shouldConstrain() {
      if (disabled) return false;
      if (!limit.reachedAny(target.get())) return false;
      if (!limit.reachedAny(location.get())) return false;
      return true;
    }
    function constrain(pointerDown) {
      if (!shouldConstrain()) return;
      const edge = limit.reachedMin(location.get()) ? 'min' : 'max';
      const diffToEdge = mathAbs(limit[edge] - location.get());
      const diffToTarget = target.get() - location.get();
      const friction = frictionLimit.constrain(diffToEdge / edgeOffsetTolerance);
      target.subtract(diffToTarget * friction);
      if (!pointerDown && mathAbs(diffToTarget) < pullBackThreshold) {
        target.set(limit.constrain(target.get()));
        scrollBody.useDuration(25).useBaseFriction();
      }
    }
    function toggleActive(active) {
      disabled = !active;
    }
    const self = {
      shouldConstrain,
      constrain,
      toggleActive
    };
    return self;
  }
  function ScrollContain(viewSize, contentSize, snapsAligned, containScroll, pixelTolerance) {
    const scrollBounds = Limit(-contentSize + viewSize, 0);
    const snapsBounded = measureBounded();
    const scrollContainLimit = findScrollContainLimit();
    const snapsContained = measureContained();
    function usePixelTolerance(bound, snap) {
      return deltaAbs(bound, snap) <= 1;
    }
    function findScrollContainLimit() {
      const startSnap = snapsBounded[0];
      const endSnap = arrayLast(snapsBounded);
      const min = snapsBounded.lastIndexOf(startSnap);
      const max = snapsBounded.indexOf(endSnap) + 1;
      return Limit(min, max);
    }
    function measureBounded() {
      return snapsAligned.map((snapAligned, index) => {
        const {
          min,
          max
        } = scrollBounds;
        const snap = scrollBounds.constrain(snapAligned);
        const isFirst = !index;
        const isLast = arrayIsLastIndex(snapsAligned, index);
        if (isFirst) return max;
        if (isLast) return min;
        if (usePixelTolerance(min, snap)) return min;
        if (usePixelTolerance(max, snap)) return max;
        return snap;
      }).map(scrollBound => parseFloat(scrollBound.toFixed(3)));
    }
    function measureContained() {
      if (contentSize <= viewSize + pixelTolerance) return [scrollBounds.max];
      if (containScroll === 'keepSnaps') return snapsBounded;
      const {
        min,
        max
      } = scrollContainLimit;
      return snapsBounded.slice(min, max);
    }
    const self = {
      snapsContained,
      scrollContainLimit
    };
    return self;
  }
  function ScrollLimit(contentSize, scrollSnaps, loop) {
    const max = scrollSnaps[0];
    const min = loop ? max - contentSize : arrayLast(scrollSnaps);
    const limit = Limit(min, max);
    const self = {
      limit
    };
    return self;
  }
  function ScrollLooper(contentSize, limit, location, vectors) {
    const jointSafety = 0.1;
    const min = limit.min + jointSafety;
    const max = limit.max + jointSafety;
    const {
      reachedMin,
      reachedMax
    } = Limit(min, max);
    function shouldLoop(direction) {
      if (direction === 1) return reachedMax(location.get());
      if (direction === -1) return reachedMin(location.get());
      return false;
    }
    function loop(direction) {
      if (!shouldLoop(direction)) return;
      const loopDistance = contentSize * (direction * -1);
      vectors.forEach(v => v.add(loopDistance));
    }
    const self = {
      loop
    };
    return self;
  }
  function ScrollProgress(limit) {
    const {
      max,
      length
    } = limit;
    function get(n) {
      const currentLocation = n - max;
      return length ? currentLocation / -length : 0;
    }
    const self = {
      get
    };
    return self;
  }
  function ScrollSnaps(axis, alignment, containerRect, slideRects, slidesToScroll) {
    const {
      startEdge,
      endEdge
    } = axis;
    const {
      groupSlides
    } = slidesToScroll;
    const alignments = measureSizes().map(alignment.measure);
    const snaps = measureUnaligned();
    const snapsAligned = measureAligned();
    function measureSizes() {
      return groupSlides(slideRects).map(rects => arrayLast(rects)[endEdge] - rects[0][startEdge]).map(mathAbs);
    }
    function measureUnaligned() {
      return slideRects.map(rect => containerRect[startEdge] - rect[startEdge]).map(snap => -mathAbs(snap));
    }
    function measureAligned() {
      return groupSlides(snaps).map(g => g[0]).map((snap, index) => snap + alignments[index]);
    }
    const self = {
      snaps,
      snapsAligned
    };
    return self;
  }
  function SlideRegistry(containSnaps, containScroll, scrollSnaps, scrollContainLimit, slidesToScroll, slideIndexes) {
    const {
      groupSlides
    } = slidesToScroll;
    const {
      min,
      max
    } = scrollContainLimit;
    const slideRegistry = createSlideRegistry();
    function createSlideRegistry() {
      const groupedSlideIndexes = groupSlides(slideIndexes);
      const doNotContain = !containSnaps || containScroll === 'keepSnaps';
      if (scrollSnaps.length === 1) return [slideIndexes];
      if (doNotContain) return groupedSlideIndexes;
      return groupedSlideIndexes.slice(min, max).map((group, index, groups) => {
        const isFirst = !index;
        const isLast = arrayIsLastIndex(groups, index);
        if (isFirst) {
          const range = arrayLast(groups[0]) + 1;
          return arrayFromNumber(range);
        }
        if (isLast) {
          const range = arrayLastIndex(slideIndexes) - arrayLast(groups)[0] + 1;
          return arrayFromNumber(range, arrayLast(groups)[0]);
        }
        return group;
      });
    }
    const self = {
      slideRegistry
    };
    return self;
  }
  function ScrollTarget(loop, scrollSnaps, contentSize, limit, targetVector) {
    const {
      reachedAny,
      removeOffset,
      constrain
    } = limit;
    function minDistance(distances) {
      return distances.concat().sort((a, b) => mathAbs(a) - mathAbs(b))[0];
    }
    function findTargetSnap(target) {
      const distance = loop ? removeOffset(target) : constrain(target);
      const ascDiffsToSnaps = scrollSnaps.map((snap, index) => ({
        diff: shortcut(snap - distance, 0),
        index
      })).sort((d1, d2) => mathAbs(d1.diff) - mathAbs(d2.diff));
      const {
        index
      } = ascDiffsToSnaps[0];
      return {
        index,
        distance
      };
    }
    function shortcut(target, direction) {
      const targets = [target, target + contentSize, target - contentSize];
      if (!loop) return target;
      if (!direction) return minDistance(targets);
      const matchingTargets = targets.filter(t => mathSign(t) === direction);
      if (matchingTargets.length) return minDistance(matchingTargets);
      return arrayLast(targets) - contentSize;
    }
    function byIndex(index, direction) {
      const diffToSnap = scrollSnaps[index] - targetVector.get();
      const distance = shortcut(diffToSnap, direction);
      return {
        index,
        distance
      };
    }
    function byDistance(distance, snap) {
      const target = targetVector.get() + distance;
      const {
        index,
        distance: targetSnapDistance
      } = findTargetSnap(target);
      const reachedBound = !loop && reachedAny(target);
      if (!snap || reachedBound) return {
        index,
        distance
      };
      const diffToSnap = scrollSnaps[index] - targetSnapDistance;
      const snapDistance = distance + shortcut(diffToSnap, 0);
      return {
        index,
        distance: snapDistance
      };
    }
    const self = {
      byDistance,
      byIndex,
      shortcut
    };
    return self;
  }
  function ScrollTo(animation, indexCurrent, indexPrevious, scrollBody, scrollTarget, targetVector, eventHandler) {
    function scrollTo(target) {
      const distanceDiff = target.distance;
      const indexDiff = target.index !== indexCurrent.get();
      targetVector.add(distanceDiff);
      if (distanceDiff) {
        if (scrollBody.duration()) {
          animation.start();
        } else {
          animation.update();
          animation.render(1);
          animation.update();
        }
      }
      if (indexDiff) {
        indexPrevious.set(indexCurrent.get());
        indexCurrent.set(target.index);
        eventHandler.emit('select');
      }
    }
    function distance(n, snap) {
      const target = scrollTarget.byDistance(n, snap);
      scrollTo(target);
    }
    function index(n, direction) {
      const targetIndex = indexCurrent.clone().set(n);
      const target = scrollTarget.byIndex(targetIndex.get(), direction);
      scrollTo(target);
    }
    const self = {
      distance,
      index
    };
    return self;
  }
  function SlideFocus(root, slides, slideRegistry, scrollTo, scrollBody, eventStore, eventHandler, watchFocus) {
    const focusListenerOptions = {
      passive: true,
      capture: true
    };
    let lastTabPressTime = 0;
    function init(emblaApi) {
      if (!watchFocus) return;
      function defaultCallback(index) {
        const nowTime = new Date().getTime();
        const diffTime = nowTime - lastTabPressTime;
        if (diffTime > 10) return;
        eventHandler.emit('slideFocusStart');
        root.scrollLeft = 0;
        const group = slideRegistry.findIndex(group => group.includes(index));
        if (!isNumber(group)) return;
        scrollBody.useDuration(0);
        scrollTo.index(group, 0);
        eventHandler.emit('slideFocus');
      }
      eventStore.add(document, 'keydown', registerTabPress, false);
      slides.forEach((slide, slideIndex) => {
        eventStore.add(slide, 'focus', evt => {
          if (isBoolean(watchFocus) || watchFocus(emblaApi, evt)) {
            defaultCallback(slideIndex);
          }
        }, focusListenerOptions);
      });
    }
    function registerTabPress(event) {
      if (event.code === 'Tab') lastTabPressTime = new Date().getTime();
    }
    const self = {
      init
    };
    return self;
  }
  function Vector1D(initialValue) {
    let value = initialValue;
    function get() {
      return value;
    }
    function set(n) {
      value = normalizeInput(n);
    }
    function add(n) {
      value += normalizeInput(n);
    }
    function subtract(n) {
      value -= normalizeInput(n);
    }
    function normalizeInput(n) {
      return isNumber(n) ? n : n.get();
    }
    const self = {
      get,
      set,
      add,
      subtract
    };
    return self;
  }
  function Translate(axis, container) {
    const translate = axis.scroll === 'x' ? x : y;
    const containerStyle = container.style;
    let previousTarget = null;
    let disabled = false;
    function x(n) {
      return `translate3d(${n}px,0px,0px)`;
    }
    function y(n) {
      return `translate3d(0px,${n}px,0px)`;
    }
    function to(target) {
      if (disabled) return;
      const newTarget = roundToTwoDecimals(axis.direction(target));
      if (newTarget === previousTarget) return;
      containerStyle.transform = translate(newTarget);
      previousTarget = newTarget;
    }
    function toggleActive(active) {
      disabled = !active;
    }
    function clear() {
      if (disabled) return;
      containerStyle.transform = '';
      if (!container.getAttribute('style')) container.removeAttribute('style');
    }
    const self = {
      clear,
      to,
      toggleActive
    };
    return self;
  }
  function SlideLooper(axis, viewSize, contentSize, slideSizes, slideSizesWithGaps, snaps, scrollSnaps, location, slides) {
    const roundingSafety = 0.5;
    const ascItems = arrayKeys(slideSizesWithGaps);
    const descItems = arrayKeys(slideSizesWithGaps).reverse();
    const loopPoints = startPoints().concat(endPoints());
    function removeSlideSizes(indexes, from) {
      return indexes.reduce((a, i) => {
        return a - slideSizesWithGaps[i];
      }, from);
    }
    function slidesInGap(indexes, gap) {
      return indexes.reduce((a, i) => {
        const remainingGap = removeSlideSizes(a, gap);
        return remainingGap > 0 ? a.concat([i]) : a;
      }, []);
    }
    function findSlideBounds(offset) {
      return snaps.map((snap, index) => ({
        start: snap - slideSizes[index] + roundingSafety + offset,
        end: snap + viewSize - roundingSafety + offset
      }));
    }
    function findLoopPoints(indexes, offset, isEndEdge) {
      const slideBounds = findSlideBounds(offset);
      return indexes.map(index => {
        const initial = isEndEdge ? 0 : -contentSize;
        const altered = isEndEdge ? contentSize : 0;
        const boundEdge = isEndEdge ? 'end' : 'start';
        const loopPoint = slideBounds[index][boundEdge];
        return {
          index,
          loopPoint,
          slideLocation: Vector1D(-1),
          translate: Translate(axis, slides[index]),
          target: () => location.get() > loopPoint ? initial : altered
        };
      });
    }
    function startPoints() {
      const gap = scrollSnaps[0];
      const indexes = slidesInGap(descItems, gap);
      return findLoopPoints(indexes, contentSize, false);
    }
    function endPoints() {
      const gap = viewSize - scrollSnaps[0] - 1;
      const indexes = slidesInGap(ascItems, gap);
      return findLoopPoints(indexes, -contentSize, true);
    }
    function canLoop() {
      return loopPoints.every(({
        index
      }) => {
        const otherIndexes = ascItems.filter(i => i !== index);
        return removeSlideSizes(otherIndexes, viewSize) <= 0.1;
      });
    }
    function loop() {
      loopPoints.forEach(loopPoint => {
        const {
          target,
          translate,
          slideLocation
        } = loopPoint;
        const shiftLocation = target();
        if (shiftLocation === slideLocation.get()) return;
        translate.to(shiftLocation);
        slideLocation.set(shiftLocation);
      });
    }
    function clear() {
      loopPoints.forEach(loopPoint => loopPoint.translate.clear());
    }
    const self = {
      canLoop,
      clear,
      loop,
      loopPoints
    };
    return self;
  }
  function SlidesHandler(container, eventHandler, watchSlides) {
    let mutationObserver;
    let destroyed = false;
    function init(emblaApi) {
      if (!watchSlides) return;
      function defaultCallback(mutations) {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            emblaApi.reInit();
            eventHandler.emit('slidesChanged');
            break;
          }
        }
      }
      mutationObserver = new MutationObserver(mutations => {
        if (destroyed) return;
        if (isBoolean(watchSlides) || watchSlides(emblaApi, mutations)) {
          defaultCallback(mutations);
        }
      });
      mutationObserver.observe(container, {
        childList: true
      });
    }
    function destroy() {
      if (mutationObserver) mutationObserver.disconnect();
      destroyed = true;
    }
    const self = {
      init,
      destroy
    };
    return self;
  }
  function SlidesInView(container, slides, eventHandler, threshold) {
    const intersectionEntryMap = {};
    let inViewCache = null;
    let notInViewCache = null;
    let intersectionObserver;
    let destroyed = false;
    function init() {
      intersectionObserver = new IntersectionObserver(entries => {
        if (destroyed) return;
        entries.forEach(entry => {
          const index = slides.indexOf(entry.target);
          intersectionEntryMap[index] = entry;
        });
        inViewCache = null;
        notInViewCache = null;
        eventHandler.emit('slidesInView');
      }, {
        root: container.parentElement,
        threshold
      });
      slides.forEach(slide => intersectionObserver.observe(slide));
    }
    function destroy() {
      if (intersectionObserver) intersectionObserver.disconnect();
      destroyed = true;
    }
    function createInViewList(inView) {
      return objectKeys(intersectionEntryMap).reduce((list, slideIndex) => {
        const index = parseInt(slideIndex);
        const {
          isIntersecting
        } = intersectionEntryMap[index];
        const inViewMatch = inView && isIntersecting;
        const notInViewMatch = !inView && !isIntersecting;
        if (inViewMatch || notInViewMatch) list.push(index);
        return list;
      }, []);
    }
    function get(inView = true) {
      if (inView && inViewCache) return inViewCache;
      if (!inView && notInViewCache) return notInViewCache;
      const slideIndexes = createInViewList(inView);
      if (inView) inViewCache = slideIndexes;
      if (!inView) notInViewCache = slideIndexes;
      return slideIndexes;
    }
    const self = {
      init,
      destroy,
      get
    };
    return self;
  }
  function SlideSizes(axis, containerRect, slideRects, slides, readEdgeGap, ownerWindow) {
    const {
      measureSize,
      startEdge,
      endEdge
    } = axis;
    const withEdgeGap = slideRects[0] && readEdgeGap;
    const startGap = measureStartGap();
    const endGap = measureEndGap();
    const slideSizes = slideRects.map(measureSize);
    const slideSizesWithGaps = measureWithGaps();
    function measureStartGap() {
      if (!withEdgeGap) return 0;
      const slideRect = slideRects[0];
      return mathAbs(containerRect[startEdge] - slideRect[startEdge]);
    }
    function measureEndGap() {
      if (!withEdgeGap) return 0;
      const style = ownerWindow.getComputedStyle(arrayLast(slides));
      return parseFloat(style.getPropertyValue(`margin-${endEdge}`));
    }
    function measureWithGaps() {
      return slideRects.map((rect, index, rects) => {
        const isFirst = !index;
        const isLast = arrayIsLastIndex(rects, index);
        if (isFirst) return slideSizes[index] + startGap;
        if (isLast) return slideSizes[index] + endGap;
        return rects[index + 1][startEdge] - rect[startEdge];
      }).map(mathAbs);
    }
    const self = {
      slideSizes,
      slideSizesWithGaps,
      startGap,
      endGap
    };
    return self;
  }
  function SlidesToScroll(axis, viewSize, slidesToScroll, loop, containerRect, slideRects, startGap, endGap, pixelTolerance) {
    const {
      startEdge,
      endEdge,
      direction
    } = axis;
    const groupByNumber = isNumber(slidesToScroll);
    function byNumber(array, groupSize) {
      return arrayKeys(array).filter(i => i % groupSize === 0).map(i => array.slice(i, i + groupSize));
    }
    function bySize(array) {
      if (!array.length) return [];
      return arrayKeys(array).reduce((groups, rectB, index) => {
        const rectA = arrayLast(groups) || 0;
        const isFirst = rectA === 0;
        const isLast = rectB === arrayLastIndex(array);
        const edgeA = containerRect[startEdge] - slideRects[rectA][startEdge];
        const edgeB = containerRect[startEdge] - slideRects[rectB][endEdge];
        const gapA = !loop && isFirst ? direction(startGap) : 0;
        const gapB = !loop && isLast ? direction(endGap) : 0;
        const chunkSize = mathAbs(edgeB - gapB - (edgeA + gapA));
        if (index && chunkSize > viewSize + pixelTolerance) groups.push(rectB);
        if (isLast) groups.push(array.length);
        return groups;
      }, []).map((currentSize, index, groups) => {
        const previousSize = Math.max(groups[index - 1] || 0);
        return array.slice(previousSize, currentSize);
      });
    }
    function groupSlides(array) {
      return groupByNumber ? byNumber(array, slidesToScroll) : bySize(array);
    }
    const self = {
      groupSlides
    };
    return self;
  }
  function Engine(root, container, slides, ownerDocument, ownerWindow, options, eventHandler) {
    const {
      align,
      axis: scrollAxis,
      direction,
      startIndex,
      loop,
      duration,
      dragFree,
      dragThreshold,
      inViewThreshold,
      slidesToScroll: groupSlides,
      skipSnaps,
      containScroll,
      watchResize,
      watchSlides,
      watchDrag,
      watchFocus
    } = options;
    const pixelTolerance = 2;
    const nodeRects = NodeRects();
    const containerRect = nodeRects.measure(container);
    const slideRects = slides.map(nodeRects.measure);
    const axis = Axis(scrollAxis, direction);
    const viewSize = axis.measureSize(containerRect);
    const percentOfView = PercentOfView(viewSize);
    const alignment = Alignment(align, viewSize);
    const containSnaps = !loop && !!containScroll;
    const readEdgeGap = loop || !!containScroll;
    const {
      slideSizes,
      slideSizesWithGaps,
      startGap,
      endGap
    } = SlideSizes(axis, containerRect, slideRects, slides, readEdgeGap, ownerWindow);
    const slidesToScroll = SlidesToScroll(axis, viewSize, groupSlides, loop, containerRect, slideRects, startGap, endGap, pixelTolerance);
    const {
      snaps,
      snapsAligned
    } = ScrollSnaps(axis, alignment, containerRect, slideRects, slidesToScroll);
    const contentSize = -arrayLast(snaps) + arrayLast(slideSizesWithGaps);
    const {
      snapsContained,
      scrollContainLimit
    } = ScrollContain(viewSize, contentSize, snapsAligned, containScroll, pixelTolerance);
    const scrollSnaps = containSnaps ? snapsContained : snapsAligned;
    const {
      limit
    } = ScrollLimit(contentSize, scrollSnaps, loop);
    const index = Counter(arrayLastIndex(scrollSnaps), startIndex, loop);
    const indexPrevious = index.clone();
    const slideIndexes = arrayKeys(slides);
    const update = ({
      dragHandler,
      scrollBody,
      scrollBounds,
      options: {
        loop
      }
    }) => {
      if (!loop) scrollBounds.constrain(dragHandler.pointerDown());
      scrollBody.seek();
    };
    const render = ({
      scrollBody,
      translate,
      location,
      offsetLocation,
      previousLocation,
      scrollLooper,
      slideLooper,
      dragHandler,
      animation,
      eventHandler,
      scrollBounds,
      options: {
        loop
      }
    }, alpha) => {
      const shouldSettle = scrollBody.settled();
      const withinBounds = !scrollBounds.shouldConstrain();
      const hasSettled = loop ? shouldSettle : shouldSettle && withinBounds;
      const hasSettledAndIdle = hasSettled && !dragHandler.pointerDown();
      if (hasSettledAndIdle) animation.stop();
      const interpolatedLocation = location.get() * alpha + previousLocation.get() * (1 - alpha);
      offsetLocation.set(interpolatedLocation);
      if (loop) {
        scrollLooper.loop(scrollBody.direction());
        slideLooper.loop();
      }
      translate.to(offsetLocation.get());
      if (hasSettledAndIdle) eventHandler.emit('settle');
      if (!hasSettled) eventHandler.emit('scroll');
    };
    const animation = Animations(ownerDocument, ownerWindow, () => update(engine), alpha => render(engine, alpha));
    const friction = 0.68;
    const startLocation = scrollSnaps[index.get()];
    const location = Vector1D(startLocation);
    const previousLocation = Vector1D(startLocation);
    const offsetLocation = Vector1D(startLocation);
    const target = Vector1D(startLocation);
    const scrollBody = ScrollBody(location, offsetLocation, previousLocation, target, duration, friction);
    const scrollTarget = ScrollTarget(loop, scrollSnaps, contentSize, limit, target);
    const scrollTo = ScrollTo(animation, index, indexPrevious, scrollBody, scrollTarget, target, eventHandler);
    const scrollProgress = ScrollProgress(limit);
    const eventStore = EventStore();
    const slidesInView = SlidesInView(container, slides, eventHandler, inViewThreshold);
    const {
      slideRegistry
    } = SlideRegistry(containSnaps, containScroll, scrollSnaps, scrollContainLimit, slidesToScroll, slideIndexes);
    const slideFocus = SlideFocus(root, slides, slideRegistry, scrollTo, scrollBody, eventStore, eventHandler, watchFocus);
    const engine = {
      ownerDocument,
      ownerWindow,
      eventHandler,
      containerRect,
      slideRects,
      animation,
      axis,
      dragHandler: DragHandler(axis, root, ownerDocument, ownerWindow, target, DragTracker(axis, ownerWindow), location, animation, scrollTo, scrollBody, scrollTarget, index, eventHandler, percentOfView, dragFree, dragThreshold, skipSnaps, friction, watchDrag),
      eventStore,
      percentOfView,
      index,
      indexPrevious,
      limit,
      location,
      offsetLocation,
      previousLocation,
      options,
      resizeHandler: ResizeHandler(container, eventHandler, ownerWindow, slides, axis, watchResize, nodeRects),
      scrollBody,
      scrollBounds: ScrollBounds(limit, offsetLocation, target, scrollBody, percentOfView),
      scrollLooper: ScrollLooper(contentSize, limit, offsetLocation, [location, offsetLocation, previousLocation, target]),
      scrollProgress,
      scrollSnapList: scrollSnaps.map(scrollProgress.get),
      scrollSnaps,
      scrollTarget,
      scrollTo,
      slideLooper: SlideLooper(axis, viewSize, contentSize, slideSizes, slideSizesWithGaps, snaps, scrollSnaps, offsetLocation, slides),
      slideFocus,
      slidesHandler: SlidesHandler(container, eventHandler, watchSlides),
      slidesInView,
      slideIndexes,
      slideRegistry,
      slidesToScroll,
      target,
      translate: Translate(axis, container)
    };
    return engine;
  }
  function EventHandler() {
    let listeners = {};
    let api;
    function init(emblaApi) {
      api = emblaApi;
    }
    function getListeners(evt) {
      return listeners[evt] || [];
    }
    function emit(evt) {
      getListeners(evt).forEach(e => e(api, evt));
      return self;
    }
    function on(evt, cb) {
      listeners[evt] = getListeners(evt).concat([cb]);
      return self;
    }
    function off(evt, cb) {
      listeners[evt] = getListeners(evt).filter(e => e !== cb);
      return self;
    }
    function clear() {
      listeners = {};
    }
    const self = {
      init,
      emit,
      off,
      on,
      clear
    };
    return self;
  }
  const defaultOptions$2 = {
    align: 'center',
    axis: 'x',
    container: null,
    slides: null,
    containScroll: 'trimSnaps',
    direction: 'ltr',
    slidesToScroll: 1,
    inViewThreshold: 0,
    breakpoints: {},
    dragFree: false,
    dragThreshold: 10,
    loop: false,
    skipSnaps: false,
    duration: 25,
    startIndex: 0,
    active: true,
    watchDrag: true,
    watchResize: true,
    watchSlides: true,
    watchFocus: true
  };
  function OptionsHandler(ownerWindow) {
    function mergeOptions(optionsA, optionsB) {
      return objectsMergeDeep(optionsA, optionsB || {});
    }
    function optionsAtMedia(options) {
      const optionsAtMedia = options.breakpoints || {};
      const matchedMediaOptions = objectKeys(optionsAtMedia).filter(media => ownerWindow.matchMedia(media).matches).map(media => optionsAtMedia[media]).reduce((a, mediaOption) => mergeOptions(a, mediaOption), {});
      return mergeOptions(options, matchedMediaOptions);
    }
    function optionsMediaQueries(optionsList) {
      return optionsList.map(options => objectKeys(options.breakpoints || {})).reduce((acc, mediaQueries) => acc.concat(mediaQueries), []).map(ownerWindow.matchMedia);
    }
    const self = {
      mergeOptions,
      optionsAtMedia,
      optionsMediaQueries
    };
    return self;
  }
  function PluginsHandler(optionsHandler) {
    let activePlugins = [];
    function init(emblaApi, plugins) {
      activePlugins = plugins.filter(({
        options
      }) => optionsHandler.optionsAtMedia(options).active !== false);
      activePlugins.forEach(plugin => plugin.init(emblaApi, optionsHandler));
      return plugins.reduce((map, plugin) => Object.assign(map, {
        [plugin.name]: plugin
      }), {});
    }
    function destroy() {
      activePlugins = activePlugins.filter(plugin => plugin.destroy());
    }
    const self = {
      init,
      destroy
    };
    return self;
  }
  function EmblaCarousel(root, userOptions, userPlugins) {
    const ownerDocument = root.ownerDocument;
    const ownerWindow = ownerDocument.defaultView;
    const optionsHandler = OptionsHandler(ownerWindow);
    const pluginsHandler = PluginsHandler(optionsHandler);
    const mediaHandlers = EventStore();
    const eventHandler = EventHandler();
    const {
      mergeOptions,
      optionsAtMedia,
      optionsMediaQueries
    } = optionsHandler;
    const {
      on,
      off,
      emit
    } = eventHandler;
    const reInit = reActivate;
    let destroyed = false;
    let engine;
    let optionsBase = mergeOptions(defaultOptions$2, EmblaCarousel.globalOptions);
    let options = mergeOptions(optionsBase);
    let pluginList = [];
    let pluginApis;
    let container;
    let slides;
    function storeElements() {
      const {
        container: userContainer,
        slides: userSlides
      } = options;
      const customContainer = isString(userContainer) ? root.querySelector(userContainer) : userContainer;
      container = customContainer || root.children[0];
      const customSlides = isString(userSlides) ? container.querySelectorAll(userSlides) : userSlides;
      slides = [].slice.call(customSlides || container.children);
    }
    function createEngine(options) {
      const engine = Engine(root, container, slides, ownerDocument, ownerWindow, options, eventHandler);
      if (options.loop && !engine.slideLooper.canLoop()) {
        const optionsWithoutLoop = Object.assign({}, options, {
          loop: false
        });
        return createEngine(optionsWithoutLoop);
      }
      return engine;
    }
    function activate(withOptions, withPlugins) {
      if (destroyed) return;
      optionsBase = mergeOptions(optionsBase, withOptions);
      options = optionsAtMedia(optionsBase);
      pluginList = withPlugins || pluginList;
      storeElements();
      engine = createEngine(options);
      optionsMediaQueries([optionsBase, ...pluginList.map(({
        options
      }) => options)]).forEach(query => mediaHandlers.add(query, 'change', reActivate));
      if (!options.active) return;
      engine.translate.to(engine.location.get());
      engine.animation.init();
      engine.slidesInView.init();
      engine.slideFocus.init(self);
      engine.eventHandler.init(self);
      engine.resizeHandler.init(self);
      engine.slidesHandler.init(self);
      if (engine.options.loop) engine.slideLooper.loop();
      if (container.offsetParent && slides.length) engine.dragHandler.init(self);
      pluginApis = pluginsHandler.init(self, pluginList);
    }
    function reActivate(withOptions, withPlugins) {
      const startIndex = selectedScrollSnap();
      deActivate();
      activate(mergeOptions({
        startIndex
      }, withOptions), withPlugins);
      eventHandler.emit('reInit');
    }
    function deActivate() {
      engine.dragHandler.destroy();
      engine.eventStore.clear();
      engine.translate.clear();
      engine.slideLooper.clear();
      engine.resizeHandler.destroy();
      engine.slidesHandler.destroy();
      engine.slidesInView.destroy();
      engine.animation.destroy();
      pluginsHandler.destroy();
      mediaHandlers.clear();
    }
    function destroy() {
      if (destroyed) return;
      destroyed = true;
      mediaHandlers.clear();
      deActivate();
      eventHandler.emit('destroy');
      eventHandler.clear();
    }
    function scrollTo(index, jump, direction) {
      if (!options.active || destroyed) return;
      engine.scrollBody.useBaseFriction().useDuration(jump === true ? 0 : options.duration);
      engine.scrollTo.index(index, direction || 0);
    }
    function scrollNext(jump) {
      const next = engine.index.add(1).get();
      scrollTo(next, jump, -1);
    }
    function scrollPrev(jump) {
      const prev = engine.index.add(-1).get();
      scrollTo(prev, jump, 1);
    }
    function canScrollNext() {
      const next = engine.index.add(1).get();
      return next !== selectedScrollSnap();
    }
    function canScrollPrev() {
      const prev = engine.index.add(-1).get();
      return prev !== selectedScrollSnap();
    }
    function scrollSnapList() {
      return engine.scrollSnapList;
    }
    function scrollProgress() {
      return engine.scrollProgress.get(engine.offsetLocation.get());
    }
    function selectedScrollSnap() {
      return engine.index.get();
    }
    function previousScrollSnap() {
      return engine.indexPrevious.get();
    }
    function slidesInView() {
      return engine.slidesInView.get();
    }
    function slidesNotInView() {
      return engine.slidesInView.get(false);
    }
    function plugins() {
      return pluginApis;
    }
    function internalEngine() {
      return engine;
    }
    function rootNode() {
      return root;
    }
    function containerNode() {
      return container;
    }
    function slideNodes() {
      return slides;
    }
    const self = {
      canScrollNext,
      canScrollPrev,
      containerNode,
      internalEngine,
      destroy,
      off,
      on,
      emit,
      plugins,
      previousScrollSnap,
      reInit,
      rootNode,
      scrollNext,
      scrollPrev,
      scrollProgress,
      scrollSnapList,
      scrollTo,
      selectedScrollSnap,
      slideNodes,
      slidesInView,
      slidesNotInView
    };
    activate(userOptions, userPlugins);
    setTimeout(() => eventHandler.emit('init'), 0);
    return self;
  }
  EmblaCarousel.globalOptions = undefined;

  const defaultOptions$1 = {
    direction: 'forward',
    speed: 2,
    startDelay: 1000,
    active: true,
    breakpoints: {},
    playOnInit: true,
    stopOnFocusIn: true,
    stopOnInteraction: true,
    stopOnMouseEnter: false,
    rootNode: null
  };
  function getAutoScrollRootNode(emblaApi, rootNode) {
    const emblaRootNode = emblaApi.rootNode();
    return rootNode && rootNode(emblaRootNode) || emblaRootNode;
  }
  function AutoScroll(userOptions = {}) {
    let options;
    let emblaApi;
    let destroyed;
    let startDelay;
    let timerId = 0;
    let autoScrollActive = false;
    let mouseIsOver = false;
    let defaultScrollBehaviour;
    function init(emblaApiInstance, optionsHandler) {
      emblaApi = emblaApiInstance;
      const {
        mergeOptions,
        optionsAtMedia
      } = optionsHandler;
      const optionsBase = mergeOptions(defaultOptions$1, AutoScroll.globalOptions);
      const allOptions = mergeOptions(optionsBase, userOptions);
      options = optionsAtMedia(allOptions);
      if (emblaApi.scrollSnapList().length <= 1) return;
      startDelay = options.startDelay;
      destroyed = false;
      defaultScrollBehaviour = emblaApi.internalEngine().scrollBody;
      const {
        eventStore
      } = emblaApi.internalEngine();
      const isDraggable = !!emblaApi.internalEngine().options.watchDrag;
      const root = getAutoScrollRootNode(emblaApi, options.rootNode);
      if (isDraggable) {
        emblaApi.on('pointerDown', pointerDown);
      }
      if (isDraggable && !options.stopOnInteraction) {
        emblaApi.on('pointerUp', pointerUp);
      }
      if (options.stopOnMouseEnter) {
        eventStore.add(root, 'mouseenter', mouseEnter);
      }
      if (options.stopOnMouseEnter && !options.stopOnInteraction) {
        eventStore.add(root, 'mouseleave', mouseLeave);
      }
      if (options.stopOnFocusIn) {
        emblaApi.on('slideFocusStart', stopAutoScroll);
      }
      if (options.stopOnFocusIn && !options.stopOnInteraction) {
        eventStore.add(emblaApi.containerNode(), 'focusout', startAutoScroll);
      }
      if (options.playOnInit) startAutoScroll();
    }
    function destroy() {
      emblaApi.off('pointerDown', pointerDown).off('pointerUp', pointerUp).off('slideFocusStart', stopAutoScroll).off('settle', settle);
      stopAutoScroll();
      destroyed = true;
      autoScrollActive = false;
    }
    function startAutoScroll() {
      if (destroyed) return;
      if (autoScrollActive) return;
      emblaApi.emit('autoScroll:play');
      const engine = emblaApi.internalEngine();
      const {
        ownerWindow
      } = engine;
      timerId = ownerWindow.setTimeout(() => {
        engine.scrollBody = createAutoScrollBehaviour(engine);
        engine.animation.start();
      }, startDelay);
      autoScrollActive = true;
    }
    function stopAutoScroll() {
      if (destroyed) return;
      if (!autoScrollActive) return;
      emblaApi.emit('autoScroll:stop');
      const engine = emblaApi.internalEngine();
      const {
        ownerWindow
      } = engine;
      engine.scrollBody = defaultScrollBehaviour;
      ownerWindow.clearTimeout(timerId);
      timerId = 0;
      autoScrollActive = false;
    }
    function createAutoScrollBehaviour(engine) {
      const {
        location,
        previousLocation,
        offsetLocation,
        target,
        scrollTarget,
        index,
        indexPrevious,
        limit: {
          reachedMin,
          reachedMax,
          constrain
        },
        options: {
          loop
        }
      } = engine;
      const directionSign = options.direction === 'forward' ? -1 : 1;
      const noop = () => self;
      let bodyVelocity = 0;
      let scrollDirection = 0;
      let rawLocation = location.get();
      let rawLocationPrevious = 0;
      let hasSettled = false;
      function seek() {
        let directionDiff = 0;
        previousLocation.set(location);
        bodyVelocity = directionSign * options.speed;
        rawLocation += bodyVelocity;
        location.add(bodyVelocity);
        target.set(location);
        directionDiff = rawLocation - rawLocationPrevious;
        scrollDirection = Math.sign(directionDiff);
        rawLocationPrevious = rawLocation;
        const currentIndex = scrollTarget.byDistance(0, false).index;
        if (index.get() !== currentIndex) {
          indexPrevious.set(index.get());
          index.set(currentIndex);
          emblaApi.emit('select');
        }
        const reachedEnd = options.direction === 'forward' ? reachedMin(offsetLocation.get()) : reachedMax(offsetLocation.get());
        if (!loop && reachedEnd) {
          hasSettled = true;
          const constrainedLocation = constrain(location.get());
          location.set(constrainedLocation);
          target.set(location);
          stopAutoScroll();
        }
        return self;
      }
      const self = {
        direction: () => scrollDirection,
        duration: () => -1,
        velocity: () => bodyVelocity,
        settled: () => hasSettled,
        seek,
        useBaseFriction: noop,
        useBaseDuration: noop,
        useFriction: noop,
        useDuration: noop
      };
      return self;
    }
    function pointerDown() {
      if (!mouseIsOver) stopAutoScroll();
    }
    function pointerUp() {
      if (!mouseIsOver) startAutoScrollOnSettle();
    }
    function mouseEnter() {
      mouseIsOver = true;
      stopAutoScroll();
    }
    function mouseLeave() {
      mouseIsOver = false;
      startAutoScroll();
    }
    function settle() {
      emblaApi.off('settle', settle);
      startAutoScroll();
    }
    function startAutoScrollOnSettle() {
      emblaApi.on('settle', settle);
    }
    function play(startDelayOverride) {
      if (typeof startDelayOverride !== 'undefined') {
        startDelay = startDelayOverride;
      }
      startAutoScroll();
    }
    function stop() {
      if (autoScrollActive) stopAutoScroll();
    }
    function reset() {
      if (autoScrollActive) {
        stopAutoScroll();
        startAutoScrollOnSettle();
      }
    }
    function isPlaying() {
      return autoScrollActive;
    }
    const self = {
      name: 'autoScroll',
      options: userOptions,
      init,
      destroy,
      play,
      stop,
      reset,
      isPlaying
    };
    return self;
  }
  AutoScroll.globalOptions = undefined;

  function _extends() {
    _extends = Object.assign || function (target) {
      for (var i = 1; i < arguments.length; i++) {
        var source = arguments[i];
        for (var key in source) {
          if (Object.prototype.hasOwnProperty.call(source, key)) {
            target[key] = source[key];
          }
        }
      }
      return target;
    };
    return _extends.apply(this, arguments);
  }
  var DECAY = 0.996;
  var projection = function projection(velocityPxMs, decay) {
    if (decay === void 0) {
      decay = DECAY;
    }
    return velocityPxMs * decay / (1 - decay);
  };
  function lastOf(array) {
    return array[array.length - 1];
  }
  function average(numbers) {
    return numbers.reduce(function (a, b) {
      return a + b;
    }) / numbers.length;
  }
  var clamp = function clamp(value, min, max) {
    return Math.min(Math.max(min, value), max);
  };
  function addVectors(v1, v2) {
    if (v1.length !== v2.length) {
      throw new Error('vectors must be same length');
    }
    return v1.map(function (val, i) {
      return val + v2[i];
    });
  }
  function absMax(numbers) {
    return Math.max.apply(Math, numbers.map(Math.abs));
  }
  function deepFreeze(o) {
    Object.freeze(o);
    Object.values(o).forEach(function (value) {
      if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
        deepFreeze(value);
      }
    });
    return o;
  }
  function EventBus() {
    var listeners = {};
    function on(type, listener) {
      listeners[type] = (listeners[type] || []).concat(listener);
      return function () {
        return off(type, listener);
      };
    }
    function off(type, listener) {
      listeners[type] = (listeners[type] || []).filter(function (l) {
        return l !== listener;
      });
    }
    function dispatch(type, data) {
      if (!(type in listeners)) return;
      listeners[type].forEach(function (l) {
        return l(data);
      });
    }
    return deepFreeze({
      on: on,
      off: off,
      dispatch: dispatch
    });
  }
  function WheelTargetObserver(eventListener) {
    var targets = [];
    var observe = function observe(target) {
      target.addEventListener('wheel', eventListener, {
        passive: false
      });
      targets.push(target);
      return function () {
        return unobserve(target);
      };
    };
    var unobserve = function unobserve(target) {
      target.removeEventListener('wheel', eventListener);
      targets = targets.filter(function (t) {
        return t !== target;
      });
    };
    var disconnect = function disconnect() {
      targets.forEach(unobserve);
    };
    return deepFreeze({
      observe: observe,
      unobserve: unobserve,
      disconnect: disconnect
    });
  }
  var LINE_HEIGHT = 16 * 1.125;
  var PAGE_HEIGHT = typeof window !== 'undefined' && window.innerHeight || 800;
  var DELTA_MODE_UNIT = [1, LINE_HEIGHT, PAGE_HEIGHT];
  function normalizeWheel(e) {
    var deltaX = e.deltaX * DELTA_MODE_UNIT[e.deltaMode];
    var deltaY = e.deltaY * DELTA_MODE_UNIT[e.deltaMode];
    var deltaZ = (e.deltaZ || 0) * DELTA_MODE_UNIT[e.deltaMode];
    return {
      timeStamp: e.timeStamp,
      axisDelta: [deltaX, deltaY, deltaZ]
    };
  }
  var reverseAll = [-1, -1, -1];
  function reverseAxisDeltaSign(wheel, reverseSign) {
    if (!reverseSign) {
      return wheel;
    }
    var multipliers = reverseSign === true ? reverseAll : reverseSign.map(function (shouldReverse) {
      return shouldReverse ? -1 : 1;
    });
    return _extends({}, wheel, {
      axisDelta: wheel.axisDelta.map(function (delta, i) {
        return delta * multipliers[i];
      })
    });
  }
  var DELTA_MAX_ABS = 700;
  var clampAxisDelta = function clampAxisDelta(wheel) {
    return _extends({}, wheel, {
      axisDelta: wheel.axisDelta.map(function (delta) {
        return clamp(delta, -DELTA_MAX_ABS, DELTA_MAX_ABS);
      })
    });
  };
  var ACC_FACTOR_MIN = 0.6;
  var ACC_FACTOR_MAX = 0.96;
  var WHEELEVENTS_TO_MERGE = 2;
  var WHEELEVENTS_TO_ANALAZE = 5;
  var configDefaults = deepFreeze({
    preventWheelAction: true,
    reverseSign: [true, true, false]
  });
  var WILL_END_TIMEOUT_DEFAULT = 400;
  function createWheelGesturesState() {
    return {
      isStarted: false,
      isStartPublished: false,
      isMomentum: false,
      startTime: 0,
      lastAbsDelta: Infinity,
      axisMovement: [0, 0, 0],
      axisVelocity: [0, 0, 0],
      accelerationFactors: [],
      scrollPoints: [],
      scrollPointsToMerge: [],
      willEndTimeout: WILL_END_TIMEOUT_DEFAULT
    };
  }
  function WheelGestures(optionsParam) {
    if (optionsParam === void 0) {
      optionsParam = {};
    }
    var _EventBus = EventBus(),
      on = _EventBus.on,
      off = _EventBus.off,
      dispatch = _EventBus.dispatch;
    var config = configDefaults;
    var state = createWheelGesturesState();
    var currentEvent;
    var negativeZeroFingerUpSpecialEvent = false;
    var prevWheelEventState;
    var feedWheel = function feedWheel(wheelEvents) {
      if (Array.isArray(wheelEvents)) {
        wheelEvents.forEach(function (wheelEvent) {
          return processWheelEventData(wheelEvent);
        });
      } else {
        processWheelEventData(wheelEvents);
      }
    };
    var updateOptions = function updateOptions(newOptions) {
      if (newOptions === void 0) {
        newOptions = {};
      }
      if (Object.values(newOptions).some(function (option) {
        return option === undefined || option === null;
      })) {
        return config;
      }
      return config = deepFreeze(_extends({}, configDefaults, config, newOptions));
    };
    var publishWheel = function publishWheel(additionalData) {
      var wheelEventState = _extends({
        event: currentEvent,
        isStart: false,
        isEnding: false,
        isMomentumCancel: false,
        isMomentum: state.isMomentum,
        axisDelta: [0, 0, 0],
        axisVelocity: state.axisVelocity,
        axisMovement: state.axisMovement,
        get axisMovementProjection() {
          return addVectors(wheelEventState.axisMovement, wheelEventState.axisVelocity.map(function (velocity) {
            return projection(velocity);
          }));
        }
      }, additionalData);
      dispatch('wheel', _extends({}, wheelEventState, {
        previous: prevWheelEventState
      }));
      prevWheelEventState = wheelEventState;
    };
    var shouldPreventDefault = function shouldPreventDefault(deltaMaxAbs, axisDelta) {
      var _config = config,
        preventWheelAction = _config.preventWheelAction;
      var deltaX = axisDelta[0],
        deltaY = axisDelta[1],
        deltaZ = axisDelta[2];
      if (typeof preventWheelAction === 'boolean') return preventWheelAction;
      switch (preventWheelAction) {
        case 'x':
          return Math.abs(deltaX) >= deltaMaxAbs;
        case 'y':
          return Math.abs(deltaY) >= deltaMaxAbs;
        case 'z':
          return Math.abs(deltaZ) >= deltaMaxAbs;
        default:
          return false;
      }
    };
    var processWheelEventData = function processWheelEventData(wheelEvent) {
      var _clampAxisDelta = clampAxisDelta(reverseAxisDeltaSign(normalizeWheel(wheelEvent), config.reverseSign)),
        axisDelta = _clampAxisDelta.axisDelta,
        timeStamp = _clampAxisDelta.timeStamp;
      var deltaMaxAbs = absMax(axisDelta);
      if (wheelEvent.preventDefault && shouldPreventDefault(deltaMaxAbs, axisDelta)) {
        wheelEvent.preventDefault();
      }
      if (!state.isStarted) {
        start();
      } else if (state.isMomentum && deltaMaxAbs > Math.max(2, state.lastAbsDelta * 2)) {
        end(true);
        start();
      }
      if (deltaMaxAbs === 0 && Object.is && Object.is(wheelEvent.deltaX, -0)) {
        negativeZeroFingerUpSpecialEvent = true;
        return;
      }
      currentEvent = wheelEvent;
      state.axisMovement = addVectors(state.axisMovement, axisDelta);
      state.lastAbsDelta = deltaMaxAbs;
      state.scrollPointsToMerge.push({
        axisDelta: axisDelta,
        timeStamp: timeStamp
      });
      mergeScrollPointsCalcVelocity();
      publishWheel({
        axisDelta: axisDelta,
        isStart: !state.isStartPublished
      });
      state.isStartPublished = true;
      willEnd();
    };
    var mergeScrollPointsCalcVelocity = function mergeScrollPointsCalcVelocity() {
      if (state.scrollPointsToMerge.length === WHEELEVENTS_TO_MERGE) {
        state.scrollPoints.unshift({
          axisDeltaSum: state.scrollPointsToMerge.map(function (b) {
            return b.axisDelta;
          }).reduce(addVectors),
          timeStamp: average(state.scrollPointsToMerge.map(function (b) {
            return b.timeStamp;
          }))
        });
        updateVelocity();
        state.scrollPointsToMerge.length = 0;
        state.scrollPoints.length = 1;
        if (!state.isMomentum) {
          detectMomentum();
        }
      } else if (!state.isStartPublished) {
        updateStartVelocity();
      }
    };
    var updateStartVelocity = function updateStartVelocity() {
      state.axisVelocity = lastOf(state.scrollPointsToMerge).axisDelta.map(function (d) {
        return d / state.willEndTimeout;
      });
    };
    var updateVelocity = function updateVelocity() {
      var _state$scrollPoints = state.scrollPoints,
        latestScrollPoint = _state$scrollPoints[0],
        prevScrollPoint = _state$scrollPoints[1];
      if (!prevScrollPoint || !latestScrollPoint) {
        return;
      }
      var deltaTime = latestScrollPoint.timeStamp - prevScrollPoint.timeStamp;
      if (deltaTime <= 0) {
        return;
      }
      var velocity = latestScrollPoint.axisDeltaSum.map(function (d) {
        return d / deltaTime;
      });
      var accelerationFactor = velocity.map(function (v, i) {
        return v / (state.axisVelocity[i] || 1);
      });
      state.axisVelocity = velocity;
      state.accelerationFactors.push(accelerationFactor);
      updateWillEndTimeout(deltaTime);
    };
    var updateWillEndTimeout = function updateWillEndTimeout(deltaTime) {
      var newTimeout = Math.ceil(deltaTime / 10) * 10 * 1.2;
      if (!state.isMomentum) {
        newTimeout = Math.max(100, newTimeout * 2);
      }
      state.willEndTimeout = Math.min(1000, Math.round(newTimeout));
    };
    var accelerationFactorInMomentumRange = function accelerationFactorInMomentumRange(accFactor) {
      if (accFactor === 0) return true;
      return accFactor <= ACC_FACTOR_MAX && accFactor >= ACC_FACTOR_MIN;
    };
    var detectMomentum = function detectMomentum() {
      if (state.accelerationFactors.length >= WHEELEVENTS_TO_ANALAZE) {
        if (negativeZeroFingerUpSpecialEvent) {
          negativeZeroFingerUpSpecialEvent = false;
          if (absMax(state.axisVelocity) >= 0.2) {
            recognizedMomentum();
            return;
          }
        }
        var recentAccelerationFactors = state.accelerationFactors.slice(WHEELEVENTS_TO_ANALAZE * -1);
        var detectedMomentum = recentAccelerationFactors.every(function (accFac) {
          var sameAccFac = !!accFac.reduce(function (f1, f2) {
            return f1 && f1 < 1 && f1 === f2 ? 1 : 0;
          });
          var bothAreInRangeOrZero = accFac.filter(accelerationFactorInMomentumRange).length === accFac.length;
          return sameAccFac || bothAreInRangeOrZero;
        });
        if (detectedMomentum) {
          recognizedMomentum();
        }
        state.accelerationFactors = recentAccelerationFactors;
      }
    };
    var recognizedMomentum = function recognizedMomentum() {
      state.isMomentum = true;
    };
    var start = function start() {
      state = createWheelGesturesState();
      state.isStarted = true;
      state.startTime = Date.now();
      prevWheelEventState = undefined;
      negativeZeroFingerUpSpecialEvent = false;
    };
    var willEnd = function () {
      var willEndId;
      return function () {
        clearTimeout(willEndId);
        willEndId = setTimeout(end, state.willEndTimeout);
      };
    }();
    var end = function end(isMomentumCancel) {
      if (isMomentumCancel === void 0) {
        isMomentumCancel = false;
      }
      if (!state.isStarted) return;
      if (state.isMomentum && isMomentumCancel) {
        publishWheel({
          isEnding: true,
          isMomentumCancel: true
        });
      } else {
        publishWheel({
          isEnding: true
        });
      }
      state.isMomentum = false;
      state.isStarted = false;
    };
    var _WheelTargetObserver = WheelTargetObserver(feedWheel),
      observe = _WheelTargetObserver.observe,
      unobserve = _WheelTargetObserver.unobserve,
      disconnect = _WheelTargetObserver.disconnect;
    updateOptions(optionsParam);
    return deepFreeze({
      on: on,
      off: off,
      observe: observe,
      unobserve: unobserve,
      disconnect: disconnect,
      feedWheel: feedWheel,
      updateOptions: updateOptions
    });
  }

  var defaultOptions = {
    active: true,
    breakpoints: {},
    wheelDraggingClass: 'is-wheel-dragging',
    forceWheelAxis: undefined,
    target: undefined
  };
  WheelGesturesPlugin.globalOptions = undefined;
  function WheelGesturesPlugin(userOptions) {
    if (userOptions === void 0) {
      userOptions = {};
    }
    var options;
    var cleanup = function cleanup() {};
    function init(embla, optionsHandler) {
      var _options$target, _options$forceWheelAx;
      var mergeOptions = optionsHandler.mergeOptions,
        optionsAtMedia = optionsHandler.optionsAtMedia;
      var optionsBase = mergeOptions(defaultOptions, WheelGesturesPlugin.globalOptions);
      var allOptions = mergeOptions(optionsBase, userOptions);
      options = optionsAtMedia(allOptions);
      var engine = embla.internalEngine();
      var targetNode = (_options$target = options.target) != null ? _options$target : embla.containerNode().parentNode;
      var wheelAxis = (_options$forceWheelAx = options.forceWheelAxis) != null ? _options$forceWheelAx : engine.options.axis;
      var wheelGestures = WheelGestures({
        preventWheelAction: wheelAxis,
        reverseSign: [true, true, false]
      });
      function updateSizeRelatedVariables() {
        scrollBoundaryThreshold = (wheelAxis === 'x' ? engine.containerRect.width : engine.containerRect.height) / 2;
      }
      var unobserveTargetNode = wheelGestures.observe(targetNode);
      var offWheel = wheelGestures.on('wheel', handleWheel);
      var isStarted = false;
      var startEvent;
      var overBoundaryAccumulation = 0;
      var scrollBoundaryThreshold = 0;
      var blockedWaitUntilGestureEnd = false;
      updateSizeRelatedVariables();
      embla.on('resize', updateSizeRelatedVariables);
      function wheelGestureStarted(state) {
        try {
          startEvent = new MouseEvent('mousedown', state.event);
          dispatchEvent(startEvent);
        } catch (e) {
          return cleanup();
        }
        isStarted = true;
        overBoundaryAccumulation = 0;
        addNativeMouseEventListeners();
        if (options.wheelDraggingClass) {
          targetNode.classList.add(options.wheelDraggingClass);
        }
      }
      function wheelGestureEnded(state) {
        isStarted = false;
        dispatchEvent(createRelativeMouseEvent('mouseup', state));
        removeNativeMouseEventListeners();
        if (options.wheelDraggingClass) {
          targetNode.classList.remove(options.wheelDraggingClass);
        }
      }
      function addNativeMouseEventListeners() {
        document.documentElement.addEventListener('mousemove', preventNativeMouseHandler, true);
        document.documentElement.addEventListener('mouseup', preventNativeMouseHandler, true);
        document.documentElement.addEventListener('mousedown', preventNativeMouseHandler, true);
      }
      function removeNativeMouseEventListeners() {
        document.documentElement.removeEventListener('mousemove', preventNativeMouseHandler, true);
        document.documentElement.removeEventListener('mouseup', preventNativeMouseHandler, true);
        document.documentElement.removeEventListener('mousedown', preventNativeMouseHandler, true);
      }
      function preventNativeMouseHandler(e) {
        if (isStarted && e.isTrusted) {
          e.stopImmediatePropagation();
        }
      }
      function createRelativeMouseEvent(type, state) {
        var moveX, moveY;
        if (wheelAxis === engine.options.axis) {
          var _state$axisMovement = state.axisMovement;
          moveX = _state$axisMovement[0];
          moveY = _state$axisMovement[1];
        } else {
          var _state$axisMovement2 = state.axisMovement;
          moveY = _state$axisMovement2[0];
          moveX = _state$axisMovement2[1];
        }
        var _checkIfAtBoundary = checkIfAtBoundary(state),
          isAtBoundary = _checkIfAtBoundary.isAtBoundary;
        if (isAtBoundary) {
          var progressRatio = Math.min(overBoundaryAccumulation / scrollBoundaryThreshold, 1);
          var dampingFactor = 0.25 + progressRatio * 0.5;
          var counterMoveSign = moveX > 0 ? -1 : 1;
          var counterMovement = overBoundaryAccumulation * counterMoveSign;
          var dampingMovement = counterMovement * dampingFactor;
          moveX += dampingMovement;
          moveY += dampingMovement;
        }
        if (!engine.options.skipSnaps && !engine.options.dragFree) {
          var maxX = engine.containerRect.width;
          var maxY = engine.containerRect.height;
          moveX = moveX < 0 ? Math.max(moveX, -maxX) : Math.min(moveX, maxX);
          moveY = moveY < 0 ? Math.max(moveY, -maxY) : Math.min(moveY, maxY);
        }
        return new MouseEvent(type, {
          clientX: startEvent.clientX + moveX,
          clientY: startEvent.clientY + moveY,
          screenX: startEvent.screenX + moveX,
          screenY: startEvent.screenY + moveY,
          movementX: moveX,
          movementY: moveY,
          button: 0,
          bubbles: true,
          cancelable: true,
          composed: true
        });
      }
      function dispatchEvent(event) {
        embla.containerNode().dispatchEvent(event);
      }
      function checkIfAtBoundary(state) {
        var _state$axisDelta = state.axisDelta,
          deltaX = _state$axisDelta[0],
          deltaY = _state$axisDelta[1];
        var scrollProgress = embla.scrollProgress();
        var canScrollNext = scrollProgress < 1;
        var canScrollPrev = scrollProgress > 0;
        var primaryAxisDelta = wheelAxis === 'x' ? deltaX : deltaY;
        var isScrollingNext = primaryAxisDelta < 0;
        var isScrollingPrev = primaryAxisDelta > 0;
        var isAtBoundary = isScrollingNext && !canScrollNext || isScrollingPrev && !canScrollPrev;
        return {
          isAtBoundary: isAtBoundary,
          primaryAxisDelta: primaryAxisDelta
        };
      }
      function isBoundaryThresholdReached(state) {
        var _checkIfAtBoundary2 = checkIfAtBoundary(state),
          isAtBoundary = _checkIfAtBoundary2.isAtBoundary,
          primaryAxisDelta = _checkIfAtBoundary2.primaryAxisDelta;
        if (isAtBoundary && !state.isMomentum) {
          overBoundaryAccumulation += Math.abs(primaryAxisDelta);
          if (overBoundaryAccumulation > scrollBoundaryThreshold) {
            blockedWaitUntilGestureEnd = true;
            wheelGestureEnded(state);
            return true;
          }
        } else {
          overBoundaryAccumulation = 0;
        }
        return false;
      }
      function handleWheel(state) {
        var _state$axisDelta2 = state.axisDelta,
          deltaX = _state$axisDelta2[0],
          deltaY = _state$axisDelta2[1];
        var primaryAxisDelta = wheelAxis === 'x' ? deltaX : deltaY;
        var crossAxisDelta = wheelAxis === 'x' ? deltaY : deltaX;
        var isRelease = state.isMomentum && state.previous && !state.previous.isMomentum;
        var isEndingOrRelease = state.isEnding && !state.isMomentum || isRelease;
        var primaryAxisDeltaIsDominant = Math.abs(primaryAxisDelta) > Math.abs(crossAxisDelta);
        if (primaryAxisDeltaIsDominant && !isStarted && !state.isMomentum && !blockedWaitUntilGestureEnd) {
          wheelGestureStarted(state);
        }
        if (blockedWaitUntilGestureEnd && state.isEnding) {
          blockedWaitUntilGestureEnd = false;
        }
        if (!isStarted) return;
        if (isBoundaryThresholdReached(state)) return;
        if (isEndingOrRelease) {
          wheelGestureEnded(state);
        } else {
          dispatchEvent(createRelativeMouseEvent('mousemove', state));
        }
      }
      cleanup = function cleanup() {
        unobserveTargetNode();
        offWheel();
        embla.off('resize', updateSizeRelatedVariables);
        removeNativeMouseEventListeners();
      };
    }
    var self = {
      name: 'wheelGestures',
      options: userOptions,
      init: init,
      destroy: function destroy() {
        return cleanup();
      }
    };
    return self;
  }

  var FOCUS_GROUP = 'data-focus-lock';
  var FOCUS_DISABLED = 'data-focus-lock-disabled';
  var FOCUS_ALLOW = 'data-no-focus-lock';
  var FOCUS_AUTO = 'data-autofocus-inside';
  var FOCUS_NO_AUTOFOCUS = 'data-no-autofocus';

  var toArray = function (a) {
    var ret = Array(a.length);
    for (var i = 0; i < a.length; ++i) {
      ret[i] = a[i];
    }
    return ret;
  };
  var asArray = function (a) {
    return Array.isArray(a) ? a : [a];
  };
  var getFirst = function (a) {
    return Array.isArray(a) ? a[0] : a;
  };

  var isElementHidden = function (node) {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return false;
    }
    var computedStyle = window.getComputedStyle(node, null);
    if (!computedStyle || !computedStyle.getPropertyValue) {
      return false;
    }
    return computedStyle.getPropertyValue('display') === 'none' || computedStyle.getPropertyValue('visibility') === 'hidden';
  };
  var getParentNode = function (node) {
    return node.parentNode && node.parentNode.nodeType === Node.DOCUMENT_FRAGMENT_NODE ? node.parentNode.host : node.parentNode;
  };
  var isTopNode = function (node) {
    return node === document || node && node.nodeType === Node.DOCUMENT_NODE;
  };
  var isVisibleUncached = function (node, checkParent) {
    return !node || isTopNode(node) || !isElementHidden(node) && checkParent(getParentNode(node));
  };
  var isVisibleCached = function (visibilityCache, node) {
    var cached = visibilityCache.get(node);
    if (cached !== undefined) {
      return cached;
    }
    var result = isVisibleUncached(node, isVisibleCached.bind(undefined, visibilityCache));
    visibilityCache.set(node, result);
    return result;
  };
  var isAutoFocusAllowedUncached = function (node, checkParent) {
    return node && !isTopNode(node) ? isAutoFocusAllowed(node) ? checkParent(getParentNode(node)) : false : true;
  };
  var isAutoFocusAllowedCached = function (cache, node) {
    var cached = cache.get(node);
    if (cached !== undefined) {
      return cached;
    }
    var result = isAutoFocusAllowedUncached(node, isAutoFocusAllowedCached.bind(undefined, cache));
    cache.set(node, result);
    return result;
  };
  var getDataset = function (node) {
    return node.dataset;
  };
  var isHTMLButtonElement = function (node) {
    return node.tagName === 'BUTTON';
  };
  var isHTMLInputElement = function (node) {
    return node.tagName === 'INPUT';
  };
  var isRadioElement = function (node) {
    return isHTMLInputElement(node) && node.type === 'radio';
  };
  var notHiddenInput = function (node) {
    return !((isHTMLInputElement(node) || isHTMLButtonElement(node)) && (node.type === 'hidden' || node.disabled));
  };
  var isAutoFocusAllowed = function (node) {
    var attribute = node.getAttribute(FOCUS_NO_AUTOFOCUS);
    return ![true, 'true', ''].includes(attribute);
  };
  var isGuard = function (node) {
    var _a;
    return Boolean(node && ((_a = getDataset(node)) === null || _a === void 0 ? void 0 : _a.focusGuard));
  };
  var isNotAGuard = function (node) {
    return !isGuard(node);
  };
  var isDefined = function (x) {
    return Boolean(x);
  };

  var tabSort = function (a, b) {
    var tabDiff = a.tabIndex - b.tabIndex;
    var indexDiff = a.index - b.index;
    if (tabDiff) {
      if (!a.tabIndex) {
        return 1;
      }
      if (!b.tabIndex) {
        return -1;
      }
    }
    return tabDiff || indexDiff;
  };
  var orderByTabIndex = function (nodes, filterNegative, keepGuards) {
    return toArray(nodes).map(function (node, index) {
      return {
        node: node,
        index: index,
        tabIndex: keepGuards && node.tabIndex === -1 ? (node.dataset || {}).focusGuard ? 0 : -1 : node.tabIndex
      };
    }).filter(function (data) {
      return !filterNegative || data.tabIndex >= 0;
    }).sort(tabSort);
  };

  var tabbables = ['button:enabled', 'select:enabled', 'textarea:enabled', 'input:enabled', 'a[href]', 'area[href]', 'summary', 'iframe', 'object', 'embed', 'audio[controls]', 'video[controls]', '[tabindex]', '[contenteditable]', '[autofocus]'];

  var queryTabbables = tabbables.join(',');
  var queryGuardTabbables = "".concat(queryTabbables, ", [data-focus-guard]");
  var getFocusablesWithShadowDom = function (parent, withGuards) {
    return toArray((parent.shadowRoot || parent).children).reduce(function (acc, child) {
      return acc.concat(child.matches(withGuards ? queryGuardTabbables : queryTabbables) ? [child] : [], getFocusablesWithShadowDom(child));
    }, []);
  };
  var getFocusablesWithIFrame = function (parent, withGuards) {
    var _a;
    if (parent instanceof HTMLIFrameElement && ((_a = parent.contentDocument) === null || _a === void 0 ? void 0 : _a.body)) {
      return getFocusables([parent.contentDocument.body], withGuards);
    }
    return [parent];
  };
  var getFocusables = function (parents, withGuards) {
    return parents.reduce(function (acc, parent) {
      var _a;
      var focusableWithShadowDom = getFocusablesWithShadowDom(parent, withGuards);
      var focusableWithIframes = (_a = []).concat.apply(_a, focusableWithShadowDom.map(function (node) {
        return getFocusablesWithIFrame(node, withGuards);
      }));
      return acc.concat(focusableWithIframes, parent.parentNode ? toArray(parent.parentNode.querySelectorAll(queryTabbables)).filter(function (node) {
        return node === parent;
      }) : []);
    }, []);
  };
  var getParentAutofocusables = function (parent) {
    var parentFocus = parent.querySelectorAll("[".concat(FOCUS_AUTO, "]"));
    return toArray(parentFocus).map(function (node) {
      return getFocusables([node]);
    }).reduce(function (acc, nodes) {
      return acc.concat(nodes);
    }, []);
  };

  var filterFocusable = function (nodes, visibilityCache) {
    return toArray(nodes).filter(function (node) {
      return isVisibleCached(visibilityCache, node);
    }).filter(function (node) {
      return notHiddenInput(node);
    });
  };
  var filterAutoFocusable = function (nodes, cache) {
    if (cache === void 0) {
      cache = new Map();
    }
    return toArray(nodes).filter(function (node) {
      return isAutoFocusAllowedCached(cache, node);
    });
  };
  var getTabbableNodes = function (topNodes, visibilityCache, withGuards) {
    return orderByTabIndex(filterFocusable(getFocusables(topNodes, withGuards), visibilityCache), true, withGuards);
  };
  var getAllTabbableNodes = function (topNodes, visibilityCache) {
    return orderByTabIndex(filterFocusable(getFocusables(topNodes), visibilityCache), false);
  };
  var parentAutofocusables = function (topNode, visibilityCache) {
    return filterFocusable(getParentAutofocusables(topNode), visibilityCache);
  };
  var contains = function (scope, element) {
    if (scope.shadowRoot) {
      return contains(scope.shadowRoot, element);
    } else {
      if (Object.getPrototypeOf(scope).contains !== undefined && Object.getPrototypeOf(scope).contains.call(scope, element)) {
        return true;
      }
      return toArray(scope.children).some(function (child) {
        var _a;
        if (child instanceof HTMLIFrameElement) {
          var iframeBody = (_a = child.contentDocument) === null || _a === void 0 ? void 0 : _a.body;
          if (iframeBody) {
            return contains(iframeBody, element);
          }
          return false;
        }
        return contains(child, element);
      });
    }
  };

  var filterNested = function (nodes) {
    var contained = new Set();
    var l = nodes.length;
    for (var i = 0; i < l; i += 1) {
      for (var j = i + 1; j < l; j += 1) {
        var position = nodes[i].compareDocumentPosition(nodes[j]);
        if ((position & Node.DOCUMENT_POSITION_CONTAINED_BY) > 0) {
          contained.add(j);
        }
        if ((position & Node.DOCUMENT_POSITION_CONTAINS) > 0) {
          contained.add(i);
        }
      }
    }
    return nodes.filter(function (_, index) {
      return !contained.has(index);
    });
  };
  var getTopParent = function (node) {
    return node.parentNode ? getTopParent(node.parentNode) : node;
  };
  var getAllAffectedNodes = function (node) {
    var nodes = asArray(node);
    return nodes.filter(Boolean).reduce(function (acc, currentNode) {
      var group = currentNode.getAttribute(FOCUS_GROUP);
      acc.push.apply(acc, group ? filterNested(toArray(getTopParent(currentNode).querySelectorAll("[".concat(FOCUS_GROUP, "=\"").concat(group, "\"]:not([").concat(FOCUS_DISABLED, "=\"disabled\"])")))) : [currentNode]);
      return acc;
    }, []);
  };

  var safeProbe = function (cb) {
    try {
      return cb();
    } catch (e) {
      return undefined;
    }
  };

  var getActiveElement = function (inDocument) {
    if (inDocument === void 0) {
      inDocument = document;
    }
    if (!inDocument || !inDocument.activeElement) {
      return undefined;
    }
    var activeElement = inDocument.activeElement;
    return activeElement.shadowRoot ? getActiveElement(activeElement.shadowRoot) : activeElement instanceof HTMLIFrameElement && safeProbe(function () {
      return activeElement.contentWindow.document;
    }) ? getActiveElement(activeElement.contentWindow.document) : activeElement;
  };

  var focusInFrame = function (frame, activeElement) {
    return frame === activeElement;
  };
  var focusInsideIframe = function (topNode, activeElement) {
    return Boolean(toArray(topNode.querySelectorAll('iframe')).some(function (node) {
      return focusInFrame(node, activeElement);
    }));
  };
  var focusInside = function (topNode, activeElement) {
    if (activeElement === void 0) {
      activeElement = getActiveElement(getFirst(topNode).ownerDocument);
    }
    if (!activeElement || activeElement.dataset && activeElement.dataset.focusGuard) {
      return false;
    }
    return getAllAffectedNodes(topNode).some(function (node) {
      return contains(node, activeElement) || focusInsideIframe(node, activeElement);
    });
  };

  var focusIsHidden = function (inDocument) {
    if (inDocument === void 0) {
      inDocument = document;
    }
    var activeElement = getActiveElement(inDocument);
    if (!activeElement) {
      return false;
    }
    return toArray(inDocument.querySelectorAll("[".concat(FOCUS_ALLOW, "]"))).some(function (node) {
      return contains(node, activeElement);
    });
  };

  var findSelectedRadio = function (node, nodes) {
    return nodes.filter(isRadioElement).filter(function (el) {
      return el.name === node.name;
    }).filter(function (el) {
      return el.checked;
    })[0] || node;
  };
  var correctNode = function (node, nodes) {
    if (isRadioElement(node) && node.name) {
      return findSelectedRadio(node, nodes);
    }
    return node;
  };
  var correctNodes = function (nodes) {
    var resultSet = new Set();
    nodes.forEach(function (node) {
      return resultSet.add(correctNode(node, nodes));
    });
    return nodes.filter(function (node) {
      return resultSet.has(node);
    });
  };

  var pickFirstFocus = function (nodes) {
    if (nodes[0] && nodes.length > 1) {
      return correctNode(nodes[0], nodes);
    }
    return nodes[0];
  };
  var pickFocusable = function (nodes, index) {
    if (nodes.length > 1) {
      return nodes.indexOf(correctNode(nodes[index], nodes));
    }
    return index;
  };

  var NEW_FOCUS = 'NEW_FOCUS';
  var newFocus = function (innerNodes, outerNodes, activeElement, lastNode) {
    var cnt = innerNodes.length;
    var firstFocus = innerNodes[0];
    var lastFocus = innerNodes[cnt - 1];
    var isOnGuard = isGuard(activeElement);
    if (activeElement && innerNodes.indexOf(activeElement) >= 0) {
      return undefined;
    }
    var activeIndex = activeElement !== undefined ? outerNodes.indexOf(activeElement) : -1;
    var lastIndex = lastNode ? outerNodes.indexOf(lastNode) : activeIndex;
    var lastNodeInside = lastNode ? innerNodes.indexOf(lastNode) : -1;
    var indexDiff = activeIndex - lastIndex;
    var firstNodeIndex = outerNodes.indexOf(firstFocus);
    var lastNodeIndex = outerNodes.indexOf(lastFocus);
    var correctedNodes = correctNodes(outerNodes);
    var correctedIndex = activeElement !== undefined ? correctedNodes.indexOf(activeElement) : -1;
    var correctedIndexDiff = correctedIndex - (lastNode ? correctedNodes.indexOf(lastNode) : activeIndex);
    var returnFirstNode = pickFocusable(innerNodes, 0);
    var returnLastNode = pickFocusable(innerNodes, cnt - 1);
    if (activeIndex === -1 || lastNodeInside === -1) {
      return NEW_FOCUS;
    }
    if (!indexDiff && lastNodeInside >= 0) {
      return lastNodeInside;
    }
    if (activeIndex <= firstNodeIndex && isOnGuard && Math.abs(indexDiff) > 1) {
      return returnLastNode;
    }
    if (activeIndex >= lastNodeIndex && isOnGuard && Math.abs(indexDiff) > 1) {
      return returnFirstNode;
    }
    if (indexDiff && Math.abs(correctedIndexDiff) > 1) {
      return lastNodeInside;
    }
    if (activeIndex <= firstNodeIndex) {
      return returnLastNode;
    }
    if (activeIndex > lastNodeIndex) {
      return returnFirstNode;
    }
    if (indexDiff) {
      if (Math.abs(indexDiff) > 1) {
        return lastNodeInside;
      }
      return (cnt + lastNodeInside + indexDiff) % cnt;
    }
    return undefined;
  };

  var findAutoFocused = function (autoFocusables) {
    return function (node) {
      var _a;
      var autofocus = (_a = getDataset(node)) === null || _a === void 0 ? void 0 : _a.autofocus;
      return (node.autofocus || autofocus !== undefined && autofocus !== 'false' || autoFocusables.indexOf(node) >= 0
      );
    };
  };
  var pickAutofocus = function (nodesIndexes, orderedNodes, groups) {
    var nodes = nodesIndexes.map(function (_a) {
      var node = _a.node;
      return node;
    });
    var autoFocusable = filterAutoFocusable(nodes.filter(findAutoFocused(groups)));
    if (autoFocusable && autoFocusable.length) {
      return pickFirstFocus(autoFocusable);
    }
    return pickFirstFocus(filterAutoFocusable(orderedNodes));
  };

  var getParents = function (node, parents) {
    if (parents === void 0) {
      parents = [];
    }
    parents.push(node);
    if (node.parentNode) {
      getParents(node.parentNode.host || node.parentNode, parents);
    }
    return parents;
  };
  var getCommonParent = function (nodeA, nodeB) {
    var parentsA = getParents(nodeA);
    var parentsB = getParents(nodeB);
    for (var i = 0; i < parentsA.length; i += 1) {
      var currentParent = parentsA[i];
      if (parentsB.indexOf(currentParent) >= 0) {
        return currentParent;
      }
    }
    return false;
  };
  var getTopCommonParent = function (baseActiveElement, leftEntry, rightEntries) {
    var activeElements = asArray(baseActiveElement);
    var leftEntries = asArray(leftEntry);
    var activeElement = activeElements[0];
    var topCommon = false;
    leftEntries.filter(Boolean).forEach(function (entry) {
      topCommon = getCommonParent(topCommon || entry, entry) || topCommon;
      rightEntries.filter(Boolean).forEach(function (subEntry) {
        var common = getCommonParent(activeElement, subEntry);
        if (common) {
          if (!topCommon || contains(common, topCommon)) {
            topCommon = common;
          } else {
            topCommon = getCommonParent(common, topCommon);
          }
        }
      });
    });
    return topCommon;
  };
  var allParentAutofocusables = function (entries, visibilityCache) {
    return entries.reduce(function (acc, node) {
      return acc.concat(parentAutofocusables(node, visibilityCache));
    }, []);
  };

  var reorderNodes = function (srcNodes, dstNodes) {
    var remap = new Map();
    dstNodes.forEach(function (entity) {
      return remap.set(entity.node, entity);
    });
    return srcNodes.map(function (node) {
      return remap.get(node);
    }).filter(isDefined);
  };
  var getFocusMerge = function (topNode, lastNode) {
    var activeElement = getActiveElement(asArray(topNode).length > 0 ? document : getFirst(topNode).ownerDocument);
    var entries = getAllAffectedNodes(topNode).filter(isNotAGuard);
    var commonParent = getTopCommonParent(activeElement || topNode, topNode, entries);
    var visibilityCache = new Map();
    var anyFocusable = getAllTabbableNodes(entries, visibilityCache);
    var innerElements = getTabbableNodes(entries, visibilityCache).filter(function (_a) {
      var node = _a.node;
      return isNotAGuard(node);
    });
    if (!innerElements[0]) {
      innerElements = anyFocusable;
      if (!innerElements[0]) {
        return undefined;
      }
    }
    var outerNodes = getAllTabbableNodes([commonParent], visibilityCache).map(function (_a) {
      var node = _a.node;
      return node;
    });
    var orderedInnerElements = reorderNodes(outerNodes, innerElements);
    var innerNodes = orderedInnerElements.map(function (_a) {
      var node = _a.node;
      return node;
    });
    var newId = newFocus(innerNodes, outerNodes, activeElement, lastNode);
    if (newId === NEW_FOCUS) {
      var focusNode = pickAutofocus(anyFocusable, innerNodes, allParentAutofocusables(entries, visibilityCache));
      if (focusNode) {
        return {
          node: focusNode
        };
      } else {
        return undefined;
      }
    }
    if (newId === undefined) {
      return newId;
    }
    return orderedInnerElements[newId];
  };

  var focusOn = function (target, focusOptions) {
    if ('focus' in target) {
      target.focus(focusOptions);
    }
    if ('contentWindow' in target && target.contentWindow) {
      target.contentWindow.focus();
    }
  };
  var guardCount = 0;
  var lockDisabled = false;
  var setFocus = function (topNode, lastNode, options) {
    if (options === void 0) {
      options = {};
    }
    var focusable = getFocusMerge(topNode, lastNode);
    if (lockDisabled) {
      return;
    }
    if (focusable) {
      if (guardCount > 2) {
        lockDisabled = true;
        setTimeout(function () {
          lockDisabled = false;
        }, 1);
        return;
      }
      guardCount++;
      focusOn(focusable.node, options.focusOptions);
      guardCount--;
    }
  };

  var moveFocusInside = setFocus;

  var lastActiveTrap = 0;
  var lastActiveFocus = null;
  var focusOnBody = function focusOnBody() {
    return document && document.activeElement === document.body;
  };
  var isFreeFocus = function isFreeFocus() {
    return focusOnBody() || focusIsHidden();
  };
  var activateTrap = function activateTrap() {
    var result = false;
    if (lastActiveTrap) {
      var observed = lastActiveTrap;
      if (!isFreeFocus()) {
        if (observed && !focusInside(observed)) {
          result = moveFocusInside(observed, lastActiveFocus);
        }
        lastActiveFocus = document.activeElement;
      }
    }
    return result;
  };
  var reducePropsToState = function reducePropsToState(propsList) {
    return propsList.filter(function (node) {
      return node;
    }).slice(-1)[0];
  };
  var handleStateChangeOnClient = function handleStateChangeOnClient(trap) {
    lastActiveTrap = trap;
    if (trap) {
      activateTrap();
    }
  };
  var instances = [];
  var emitChange = function emitChange(event) {
    if (handleStateChangeOnClient(reducePropsToState(instances))) {
      event && event.preventDefault();
      return true;
    }
    return false;
  };
  var attachHandler = function attachHandler() {
    document.addEventListener('focusin', emitChange);
  };
  var detachHandler = function detachHandler() {
    document.removeEventListener('focusin', emitChange);
  };
  var focusLock = {
    on: function on(domNode) {
      if (instances.length === 0) {
        attachHandler();
      }
      if (instances.indexOf(domNode) < 0) {
        instances.push(domNode);
        emitChange();
      }
    },
    off: function off(domNode) {
      instances = instances.filter(function (node) {
        return node !== domNode;
      });
      emitChange();
      if (instances.length === 0) {
        detachHandler();
      }
    }
  };

  window.PoleneVendors = {
    EmblaCarousel,
    EmblaPlugins: {
      AutoScroll,
      WheelGestures: WheelGesturesPlugin
    },
    focusLock
  };

})();
